import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Send, Instagram, Facebook } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import SoapyButton from "@/components/landing/SoapyButton";
import { SiteHeading } from "@/lib/siteContent";

const contactInfo = [
  { icon: Phone, label: "Phone", value: "(520) 380-4407", href: "tel:5203804407" },
  { icon: Mail, label: "Email", value: "Reflectionsautodetailingaz@gmail.com", href: "mailto:Reflectionsautodetailingaz@gmail.com" },
  { icon: MapPin, label: "Area", value: "Maricopa, AZ & Surrounding", href: null },
  { icon: Clock, label: "Hours", value: "Available 24/7", href: null },
];

const services = ["Full Interior Detail", "Full Exterior Detail", "Paint Correction", "Ceramic Seal", "5-Year Ceramic", "Other"];

const Contact = () => {
  const [formData, setFormData] = useState({ firstName: "", lastName: "", phone: "", email: "", service: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise(r => setTimeout(r, 1500));
    toast.success("Quote request submitted!", { description: "We'll get back to you within 24 hours." });
    setFormData({ firstName: "", lastName: "", phone: "", email: "", service: "", message: "" });
    setIsSubmitting(false);
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-16 sm:py-24 lg:py-32 bg-[#0A0A0A] relative overflow-hidden">
      {/* Background */}
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-sky-500/10 rounded-full blur-[120px]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-sky-500 text-xs tracking-[0.3em] uppercase mb-4 block">Contact</span>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl text-white mb-4">
            <SiteHeading k="contact.title" />
          </h2>
          <p className="text-gray-500">We'll get back to you within 24 hours</p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-2 space-y-6"
          >
            {contactInfo.map((item, i) => {
              const Icon = item.icon;
              const content = (
                <motion.div
                  className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/5 hover:border-sky-500/30 transition-colors"
                  whileHover={{ x: 5 }}
                >
                  <div className="w-12 h-12 rounded-xl bg-sky-500/20 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-sky-400" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">{item.label}</p>
                    <p className="text-white font-medium text-sm">{item.value}</p>
                  </div>
                </motion.div>
              );
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
                  {item.href ? <a href={item.href}>{content}</a> : content}
                </motion.div>
              );
            })}

            {/* Social */}
            <div className="pt-6 border-t border-white/10">
              <p className="text-gray-500 text-sm mb-4">Follow us</p>
              <div className="flex gap-3">
                {[
                  { Icon: Instagram, href: "https://www.instagram.com/reflections.details/" },
                  { Icon: Facebook, href: "https://www.facebook.com/reflections.detailing.677206" },
                ].map(({ Icon, href }, i) => (
                  <motion.a
                    key={i}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-sky-400 hover:border-sky-500/30 transition-all"
                    whileHover={{ scale: 1.1, y: -2 }}
                  >
                    <Icon className="w-5 h-5" />
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-3"
          >
            <form onSubmit={handleSubmit} data-testid="contact-form" className="bg-gradient-to-b from-white/5 to-transparent border border-white/10 rounded-2xl p-6 sm:p-8 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-gray-400 text-sm">First Name</Label>
                  <Input
                    data-testid="input-firstname"
                    value={formData.firstName}
                    onChange={e => setFormData(p => ({ ...p, firstName: e.target.value }))}
                    required
                    className="bg-black/50 border-white/10 focus:border-sky-500 text-white"
                    placeholder="John"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400 text-sm">Last Name</Label>
                  <Input
                    data-testid="input-lastname"
                    value={formData.lastName}
                    onChange={e => setFormData(p => ({ ...p, lastName: e.target.value }))}
                    required
                    className="bg-black/50 border-white/10 focus:border-sky-500 text-white"
                    placeholder="Doe"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-gray-400 text-sm">Phone</Label>
                  <Input
                    data-testid="input-phone"
                    type="tel"
                    value={formData.phone}
                    onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                    required
                    className="bg-black/50 border-white/10 focus:border-sky-500 text-white"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400 text-sm">Email</Label>
                  <Input
                    data-testid="input-email"
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                    className="bg-black/50 border-white/10 focus:border-sky-500 text-white"
                    placeholder="john@example.com"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400 text-sm">Service</Label>
                <Select value={formData.service} onValueChange={v => setFormData(p => ({ ...p, service: v }))}>
                  <SelectTrigger data-testid="select-service" className="bg-black/50 border-white/10 focus:border-sky-500 text-white">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111] border-white/10">
                    {services.map(s => <SelectItem key={s} value={s} className="text-white hover:bg-sky-500/20">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400 text-sm">Message</Label>
                <Textarea
                  data-testid="input-message"
                  value={formData.message}
                  onChange={e => setFormData(p => ({ ...p, message: e.target.value }))}
                  rows={4}
                  className="bg-black/50 border-white/10 focus:border-sky-500 text-white resize-none"
                  placeholder="Tell us about your vehicle..."
                />
              </div>
              <SoapyButton
                type="submit"
                data-testid="contact-submit"
                disabled={isSubmitting}
                className="w-full bg-sky-500 hover:bg-sky-400 text-white py-5 text-lg"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">Submit Request <Send className="w-5 h-5" /></span>
                )}
              </SoapyButton>
            </form>
          </motion.div>
        </div>

        {/* Shop locations — map embeds */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 sm:mt-24"
        >
          <div className="text-center mb-8">
            <span className="text-sky-500 text-xs tracking-[0.3em] uppercase mb-3 block">Find Us</span>
            <h3 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl text-white">
              Two <span className="gradient-text">Locations</span>
            </h3>
            <p className="text-gray-400 text-sm mt-2">
              Mobile service to Maricopa &amp; surrounding cities, or drop off at either shop.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                name: "Maricopa Shop",
                address: "Maricopa, AZ",
                hours: "Mon–Sat · 8am–6pm",
                mapSrc: "https://www.google.com/maps?q=Maricopa,AZ&hl=en&z=12&output=embed",
                mapLink: "https://www.google.com/maps/place/Maricopa,+AZ",
              },
              {
                name: "Glendale Shop",
                address: "Glendale, AZ",
                hours: "Mon–Sat · 8am–6pm",
                mapSrc: "https://www.google.com/maps?q=Glendale,AZ&hl=en&z=12&output=embed",
                mapLink: "https://www.google.com/maps/place/Glendale,+AZ",
              },
            ].map((loc) => (
              <div
                key={loc.name}
                className="rounded-2xl overflow-hidden border border-white/10 bg-white/[0.02] hover:border-sky-500/30 transition-colors"
                data-testid={`location-${loc.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="relative w-full aspect-[16/10] bg-black/40">
                  <iframe
                    src={loc.mapSrc}
                    title={`Map of ${loc.name}`}
                    className="absolute inset-0 w-full h-full"
                    style={{ border: 0, filter: "grayscale(20%) contrast(1.05) brightness(0.92)" }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
                <div className="p-5 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-white font-medium font-['Outfit'] text-lg">{loc.name}</p>
                    <p className="text-gray-400 text-sm truncate">{loc.address}</p>
                    <p className="text-sky-300 text-xs mt-1">{loc.hours}</p>
                  </div>
                  <a
                    href={loc.mapLink}
                    target="_blank"
                    rel="noreferrer"
                    className="shrink-0 px-3 py-2 rounded-lg bg-sky-500/10 hover:bg-sky-500/20 text-sky-300 text-xs font-medium transition-colors"
                  >
                    Open in Maps →
                  </a>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
